// Library Methods
import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { InfoIcon, WarningIcon } from "src/assets/icons";
import { ACCENT_TEAL } from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

export const GUIDE_NAMESPACE = "data-hub-guide";
export const GUIDE_SECTION_PREFIX = "data-hub-guide-";

export const scrollToGuideSection = (section: string) =>
  document
    .getElementById(`${GUIDE_SECTION_PREFIX}${section}`)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });

const SectionLink = ({
  section,
  children,
}: {
  section: string;
  children?: ReactNode;
}) => (
  <Box
    component="button"
    type="button"
    onClick={() => scrollToGuideSection(section)}
    sx={{
      font: "inherit",
      color: ACCENT_TEAL,
      fontWeight: 700,
      textDecoration: "underline",
      background: "none",
      border: 0,
      padding: 0,
      cursor: "pointer",
    }}
  >
    {children}
  </Box>
);

export const Term = ({
  definition,
  children,
}: {
  definition: string;
  children?: ReactNode;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Tooltip title={t(definition)} arrow enterTouchDelay={0}>
      <Box
        component="span"
        tabIndex={0}
        sx={{
          borderBottom: `1.5px dotted ${ACCENT_TEAL}`,
          cursor: "help",
          outline: "none",
          "&:focus-visible": { outline: "2px solid #35BCFD" },
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

const UiLabel = ({ children }: { children?: ReactNode }) => {
  const theme = useTheme();

  return (
    <Box
      component="span"
      sx={{
        fontWeight: 700,
        whiteSpace: "nowrap",
        padding: "1px 6px",
        borderRadius: "4px",
        fontSize: "0.93em",
        backgroundColor:
          theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
      }}
    >
      {children}
    </Box>
  );
};

export const GUIDE_TEXT_TAGS = {
  b: <b />,
  i: <i />,
  code: <Box component="code" sx={{ fontFamily: "Consolas, monospace" }} />,
  ui: <UiLabel />,
  toinstall: <SectionLink section="install" />,
  toproblems: <SectionLink section="problems" />,
  toupdate: <SectionLink section="update" />,
  fixedip: (
    <Term definition="A number like 10.0.0.15 that identifies a computer on your clinic's network. 'Fixed' means it never changes." />
  ),
  pacserver: (
    <Term definition="The clinic's image archive, for example FORUM or Harmony. It stores exams from many machines in one place." />
  ),
  dicom: (
    <Term definition="The standard language eye-care machines and archives use to send exams to each other." />
  ),
  csv: (
    <Term definition="A simple table saved as text. Spreadsheet programs like Excel can open it." />
  ),
};

export const Rich = ({ text }: { text: string }) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return <Trans t={t} i18nKey={text} components={GUIDE_TEXT_TAGS} />;
};

export const Paragraph = ({
  text,
  lead = false,
}: {
  text: string;
  lead?: boolean;
}) => (
  <Typography fontSize={lead ? "15.5px" : "14px"} lineHeight={1.65}>
    <Rich text={text} />
  </Typography>
);

export const SubHeading = ({ text }: { text: string }) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Typography component="h3" fontSize="16px" fontWeight={700} mt="10px">
      {t(text)}
    </Typography>
  );
};

export const Bullets = ({ items }: { items: Array<string> }) => (
  <Box
    component="ul"
    sx={{
      margin: 0,
      paddingLeft: "22px",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    }}
  >
    {items.map((item) => (
      <Typography component="li" key={item} fontSize="14px" lineHeight={1.6}>
        <Rich text={item} />
      </Typography>
    ))}
  </Box>
);

export const Callout = ({
  tone,
  text,
}: {
  tone: "tip" | "warning";
  text: string;
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
        padding: "12px 14px",
        borderRadius: "8px",
        border: tone === "warning" ? "1px solid #FFC48A" : "none",
        backgroundColor: isDark ? "#4F5266" : "rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        display="flex"
        mt="2px"
        color={tone === "warning" ? "#E2772E" : ACCENT_TEAL}
      >
        {tone === "warning" ? (
          <WarningIcon width="20px" height="20px" />
        ) : (
          <InfoIcon width="20px" height="20px" />
        )}
      </Box>
      <Typography fontSize="13.5px" lineHeight={1.55}>
        <Rich text={text} />
      </Typography>
    </Box>
  );
};

export type WhoKind = "you" | "it";

export const Who = ({ kind, text }: { kind: WhoKind; text: string }) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        fontSize: "12px",
        fontWeight: 700,
        borderRadius: "999px",
        padding: "3px 10px",
        whiteSpace: "nowrap",
        ...(kind === "you"
          ? { backgroundColor: "#7FD9C2", color: "#0B3B30" }
          : {
              backgroundColor: "#202338",
              color: "#FFF",
              outline: "1px solid #5B6D7A",
            }),
      }}
    >
      {t(text)}
    </Box>
  );
};

export const WhoRow = ({
  items,
}: {
  items: Array<{ kind: WhoKind; text: string }>;
}) => (
  <Box display="flex" gap="8px" flexWrap="wrap">
    {items.map((item) => (
      <Who key={item.text} kind={item.kind} text={item.text} />
    ))}
  </Box>
);

export const GuideTable = ({
  columns,
  rows,
}: {
  columns: Array<string>;
  rows: Array<Array<ReactNode>>;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { surface, mutedText, dividerBorder } = getSurfaceTokens(theme);

  return (
    <Box
      sx={{
        overflowX: "auto",
        border: dividerBorder,
        borderRadius: "8px",
        backgroundColor: surface,
      }}
    >
      <Box
        component="table"
        sx={{
          borderCollapse: "collapse",
          width: "100%",
          minWidth: "560px",
          fontSize: "13.5px",
          lineHeight: 1.5,
          "& th": {
            textAlign: "left",
            fontSize: "12px",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: mutedText,
            padding: "10px 12px",
            borderBottom: dividerBorder,
          },
          "& td": {
            padding: "10px 12px",
            borderBottom: dividerBorder,
            verticalAlign: "top",
          },
          "& tr:last-child td": { borderBottom: 0 },
          "& td:first-of-type": { minWidth: "190px" },
        }}
      >
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{t(column)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, rowIndex) => (
            <tr key={rowIndex}>
              {cells.map((cell, cellIndex) => (
                <td key={cellIndex}>
                  {typeof cell === "string" ? <Rich text={cell} /> : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
};

export const Chapter = ({
  id,
  eyebrow,
  icon,
  title,
  children,
}: {
  id: string;
  eyebrow: string;
  icon?: ReactNode;
  title: string;
  children: ReactNode;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { dividerBorder } = getSurfaceTokens(theme);

  return (
    <Box
      component="section"
      id={`${GUIDE_SECTION_PREFIX}${id}`}
      aria-labelledby={`${GUIDE_SECTION_PREFIX}${id}-title`}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        paddingBlock: "12px 30px",
        borderBottom: dividerBorder,
        scrollMarginTop: "16px",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap="8px"
        color={ACCENT_TEAL}
        fontSize="12px"
        fontWeight={700}
        letterSpacing="0.08em"
        textTransform="uppercase"
      >
        {icon}
        {t(eyebrow)}
      </Box>
      <Typography
        component="h2"
        id={`${GUIDE_SECTION_PREFIX}${id}-title`}
        fontSize="22px"
        fontWeight={700}
        lineHeight={1.25}
        sx={{ textWrap: "balance" }}
      >
        {t(title)}
      </Typography>
      {children}
    </Box>
  );
};
